import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg } from "@/lib/api";
import FieldInput from "@/components/dash/FieldInput";
import { emptyForm, toForm, toPayload, useFieldOptions } from "@/components/dash/crudUtils";

function CrudForm({ fields, form, setForm, options, onSubmit, saving, title, onClose, testid }) {
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => (
            <div key={f.name} className="space-y-1.5">
              {f.type !== "checkbox" && <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>}
              <FieldInput field={f} form={form} onChange={patch} options={options[f.name] || f.options || []} />
              {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid={`${testid}-cancel-button`}>Batal</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900" data-testid={`${testid}-save-button`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CrudManager({ endpoint, fields, columns, testid, entity = "Data", canWrite = true, rowActions, filterRows, toolbar }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const options = useFieldOptions(fields);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const lookup = useCallback((name, val) => {
    const list = options[name] || fields.find((f) => f.name === name)?.options || [];
    return list.find((o) => String(o.value) === String(val))?.label;
  }, [options, fields]);

  const visible = useMemo(() => {
    let r = filterRows ? rows.filter(filterRows) : rows;
    if (q) r = r.filter((x) => JSON.stringify(x).toLowerCase().includes(q.toLowerCase()));
    return r;
  }, [rows, q, filterRows]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(fields, form);
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(`${entity} tersimpan`);
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus ${entity.toLowerCase()} ini?`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success(`${entity} dihapus`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`${testid}-manager`}>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="pl-9 w-56" data-testid={`${testid}-search-input`} />
          </div>
          {toolbar}
        </div>
        {canWrite && (
          <Button onClick={() => { setEditing(null); setForm(emptyForm(fields)); }} className="bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid={`${testid}-add-button`}>
            <Plus className="h-4 w-4 mr-1" />Tambah {entity}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c.label} className="text-xs uppercase tracking-wider">{c.label}</TableHead>)}
              <TableHead className="text-right text-xs uppercase tracking-wider">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline text-amber-500" /></TableCell></TableRow>}
            {!loading && visible.length === 0 && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-sm text-slate-500" data-testid={`${testid}-empty`}>Belum ada data</TableCell></TableRow>}
            {visible.map((row) => (
              <TableRow key={row.id} data-testid={`${testid}-row-${row.id}`}>
                {columns.map((c) => <TableCell key={c.label} className="text-sm align-top">{c.render ? c.render(row, lookup) : row[c.key]}</TableCell>)}
                <TableCell className="text-right whitespace-nowrap align-top">
                  {rowActions?.(row)}
                  {canWrite && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(row); setForm(toForm(fields, row)); }} data-testid={`${testid}-edit-${row.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(row)} data-testid={`${testid}-delete-${row.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {form && (
        <CrudForm fields={fields} form={form} setForm={setForm} options={options} onSubmit={save} saving={saving}
          title={`${editing ? "Ubah" : "Tambah"} ${entity}`} onClose={() => setForm(null)} testid={testid} />
      )}
    </div>
  );
}
